import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { Hub } from '@aws-amplify/core'
import { authBootstrapFailed, signedOut } from '../actions'
import { runAuthBootstrap } from '../bootstrapSession'
import handleAuthProcessError from '../handleAuthProcessError'
import { reportAuthNoUserSession } from '../authDiagnostics'
import performSignOutCleanup from '../signOutCleanup'
import { resolveAuthenticatedUserWithRetry } from '../sessionUser'

/** Hub listener + post-login Redux bootstrap; not a React context provider. */
export default function AuthSessionBootstrap({ children }) {
  const dispatch = useDispatch()
  const bootstrapGeneration = useRef(0)

  useEffect(() => {
    let cancelled = false

    async function bootstrapFromSession(reason) {
      const generation = ++bootstrapGeneration.current
      try {
        const { user, attempts } = await resolveAuthenticatedUserWithRetry()
        if (cancelled || generation !== bootstrapGeneration.current) {
          return
        }
        if (!user) {
          reportAuthNoUserSession(reason || 'bootstrap_retry_exhausted', { attempts })
          dispatch(
            authBootstrapFailed(
              'Could not resolve your account from the current sign-in session. Please sign in again.',
            ),
          )
          return
        }
        await runAuthBootstrap(dispatch, user, { reason })
      } catch (e) {
        if (!cancelled) {
          handleAuthProcessError({
            payload: { message: 'failed to get cognito user', data: e },
          })
        }
      }
    }

    bootstrapFromSession('continue_session')

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const event = payload?.event
      if (event === 'signIn' || event === 'signedIn') {
        bootstrapFromSession('hub_sign_in')
      }
      if (event === 'signOut' || event === 'signedOut') {
        performSignOutCleanup()
        dispatch(signedOut())
      }
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [dispatch])

  return children
}
